import { OrderCancellationAnalyticsApi } from '@/core/generated/api';
import { MediaLibraryApi } from '@/core/generated/api';
import { ChatReplyTemplatesApi } from '@/core/generated/api';
import { CacheManagementApi } from '@/core/generated/api';
import { MessageTemplatesApi } from '@/core/generated/api';
import { ChatPersonalizationApi } from '@/core/generated/api';
import { VoxSettingsApi } from '@/core/generated/api';
import {
    DialogTemplatesApi,
    DialogBroadcastsApi,
    AccessManagementApi,
    AuditApi,
    AdminAccessApi,
    AddressApi,
    AppVersionApi,
    AuthApi,
    BlogApi,
    BonusAccountApi,
    CitiesApi,
    CashbackLevelApi,
    CashbackRulesAdminApi,
    AppChatApi,
    ChatApi,
    ClientAddressApi,
    ClientApi,
    ClientSubscriptionApi,
    Configuration,
    EnumApi,
    EmailVerificationApi,
    ExecutorAddressApi,
    ExecutorApi,
    FeatureTogglesApi,
    SchedulerApi,
    HabitsApi,
    EventApi,
    MediaApi,
    MemoryDiagnosticsApi,
    MessageSendStatisticsApi,
    NotificationTemplateApi,
    HelpPushApi,
    NotificationApi,
    OrderApi,
    OrderProblemsApi,
    OperatorTasksApi,
    ProblemAddressesApi,
    ProblemExecutorsApi,
    OrderTaskApi,
    PaymentsApi,
    PetsApi,
    PoliciesApi,
    PriceListApi,
    PricingPeriodsApi,
    PromotionBannerTemplatesApi,
    ProductApi,
    PromoCodeApi,
    PromoCodeTemplateApi,
    PromoCodePrintingApi,
    ReviewsApi,
    SubscriptionApi,
    TagApi,
    TelegramAssistantApi,
    TelegramSettingsApi,
    TelegramSettingsAdminApi,
    VkAssistantApi,
    VoxServiceApi,
} from "../generated/api";
import type { AxiosInstance } from 'axios';
import { API_CONFIG } from "./config";
import { apiHttpClient } from './axiosInterceptor';
import { getAccessToken } from '@/core/utils/tokenUtils';

const apiCache = new Map<string, object>();

/** Возвращает клиента конфигураций диалогов */
export const getDialogTemplatesApi = (): DialogTemplatesApi => getApiInstance('DialogTemplatesApi', DialogTemplatesApi);

/** Возвращает клиента выпусков диалогов */
export const getDialogBroadcastsApi = (): DialogBroadcastsApi => getApiInstance('DialogBroadcastsApi', DialogBroadcastsApi);

/** Возвращает клиента чтения настроек Telegram */
export const getTelegramSettingsApi = (): TelegramSettingsApi =>
    getApiInstance('TelegramSettingsApi', TelegramSettingsApi);

/** Возвращает клиента изменения настроек Telegram */
export const getTelegramSettingsAdminApi = (): TelegramSettingsAdminApi =>
    getApiInstance('TelegramSettingsAdminApi', TelegramSettingsAdminApi);

/** Возвращает клиента общего задачника операторов */
export const getOperatorTasksApi = (): OperatorTasksApi =>
    getApiInstance('OperatorTasksApi', OperatorTasksApi);

/** Возвращает клиента проверки координат домов */
export const getProblemAddressesApi = (): ProblemAddressesApi =>
    getApiInstance('ProblemAddressesApi', ProblemAddressesApi);

/** Возвращает клиента отзывов с низкими оценками */
export const getProblemExecutorsApi = (): ProblemExecutorsApi =>
    getApiInstance('ProblemExecutorsApi', ProblemExecutorsApi);

/** Возвращает клиента диагностики проблемных заказов */
export const getOrderProblemsApi = (): OrderProblemsApi =>
    getApiInstance('OrderProblemsApi', OrderProblemsApi);

/** Возвращает клиента подтверждения электронной почты */
export const getEmailVerificationApi = (): EmailVerificationApi =>
    getApiInstance('EmailVerificationApi', EmailVerificationApi);

/** Возвращает клиента возможностей административного интерфейса */
export const getAdminAccessApi = (): AdminAccessApi =>
    getApiInstance('AdminAccessApi', AdminAccessApi);

/** Возвращает клиента статистики отправок */
export const getMessageSendStatisticsApi = (): MessageSendStatisticsApi =>
    getApiInstance('MessageSendStatisticsApi', MessageSendStatisticsApi);

/** Возвращает клиента функциональных переключателей. */
export const getFeatureTogglesApi = (): FeatureTogglesApi => getApiInstance('FeatureTogglesApi', FeatureTogglesApi);

/** Подготавливает параметры подключения текущего сотрудника */
const createConfig = (): Configuration => {
    return new Configuration({
        basePath: API_CONFIG.baseURL,
        accessToken: () => getAccessToken() || '',
    });
};

/** Возвращает клиент API из общего кеша. */
function getApiInstance<T extends object>(
    key: string,
    ApiClass: new (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) => T,
): T {
    let instance = apiCache.get(key);

    if (!instance) {
        instance = new ApiClass(createConfig(), undefined, apiHttpClient);
        apiCache.set(key, instance);
    } else {
        const newConfig = createConfig();

        if (Reflect.has(instance, 'configuration')) {
            Reflect.set(instance, 'configuration', newConfig);
        }

        const axios = Reflect.get(instance, 'axios') as AxiosInstance | undefined;

        if (axios) {
            const token = getAccessToken();

            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
        }
    }

    return instance as T;
}

/** Возвращает клиента API питомцев */
export const getPetsApi = (): PetsApi => {
    return getApiInstance('PetsApi', PetsApi);
};

/** Возвращает клиента управления правами и доступами. */
export const getAccessManagementApi = (): AccessManagementApi => {
    return getApiInstance('AccessManagementApi', AccessManagementApi);
};

/** Возвращает клиент истории изменений */
export const getAuditApi = (): AuditApi => getApiInstance('AuditApi', AuditApi);

/** Возвращает клиента ссылок для привязки Telegram. */
export const getTelegramAssistantApi = (): TelegramAssistantApi => {
    return getApiInstance('TelegramAssistantApi', TelegramAssistantApi);
};

/** Возвращает клиента ссылок для привязки VK. */
export const getVkAssistantApi = (): VkAssistantApi => {
    return getApiInstance('VkAssistantApi', VkAssistantApi);
};

/** Возвращает клиента API клиентов */
export const getClientApi = (): ClientApi => {
    return getApiInstance('ClientApi', ClientApi);
};

/** Возвращает клиента API авторизации */
export const getAuthApi = (): AuthApi => {
    return getApiInstance('AuthApi', AuthApi);
};

/** Возвращает клиента API адресов */
export const getAddressApi = (): AddressApi => {
    return getApiInstance('AddressApi', AddressApi);
};

/** Возвращает клиента API версий приложения */
export const getAppVersionApi = (): AppVersionApi => {
    return getApiInstance('AppVersionApi', AppVersionApi);
};

/** Возвращает клиента для фиксированных тегов. */
export const getTagApi = (): TagApi => {
    return getApiInstance('TagApi', TagApi);
};

/** Возвращает клиента API услуг VOX */
export const getVoxServiceApi = (): VoxServiceApi => {
    return getApiInstance('VoxServiceApi', VoxServiceApi);
};

/** Возвращает клиента API адресов клиентов */
export const getClientAddressApi = (): ClientAddressApi => {
    return getApiInstance('ClientAddressApi', ClientAddressApi);
};

/** Возвращает клиента API подписок клиентов */
export const getClientSubscriptionApi = (): ClientSubscriptionApi => {
    return getApiInstance('ClientSubscriptionApi', ClientSubscriptionApi);
};

/** Возвращает клиента API продуктов */
export const getProductApi = (): ProductApi => {
    return getApiInstance('ProductApi', ProductApi);
};

/** Возвращает клиента API уровней кэшбэка */
export const getCashbackLevelApi = (): CashbackLevelApi => {
    return getApiInstance('CashbackLevelApi', CashbackLevelApi);
};

/** Возвращает клиент административных правил кэшбэка. */
export const getCashbackRulesAdminApi = (): CashbackRulesAdminApi => getApiInstance('CashbackRulesAdminApi', CashbackRulesAdminApi);

/** Возвращает клиента API бонусных счетов */
export const getBonusAccountApi = (): BonusAccountApi => {
    return getApiInstance('BonusAccountApi', BonusAccountApi);
};

/** Возвращает generated-клиент операций чата. */
export const getChatApi = (): ChatApi => {
    return getApiInstance('ChatApi', ChatApi);
};

/** Возвращает клиента API событий */
export const getEventApi = (): EventApi => {
    return getApiInstance('EventApi', EventApi);
};

/** Возвращает клиента API шаблонов уведомлений */
export const getNotificationTemplateApi = (): NotificationTemplateApi => {
    return getApiInstance('NotificationTemplateApi', NotificationTemplateApi);
};

/** Возвращает клиента API городов */
export const getCitiesApi = (): CitiesApi => {
    return getApiInstance('CitiesApi', CitiesApi);
};

/** Возвращает клиента API привычек */
export const getHabitsApi = (): HabitsApi => {
    return getApiInstance('HabitsApi', HabitsApi);
};

/** Возвращает клиента API статей блога */
export const getBlogApi = (): BlogApi => {
    return getApiInstance('BlogApi', BlogApi);
};

/** Возвращает клиента API исполнителей */
export const getExecutorApi = (): ExecutorApi => {
    return getApiInstance('ExecutorApi', ExecutorApi);
};

/** Возвращает клиента API адресов исполнителей */
export const getExecutorAddressApi = (): ExecutorAddressApi => {
    return getApiInstance('ExecutorAddressApi', ExecutorAddressApi);
};

/** Возвращает клиента API заказов */
export const getOrderApi = (): OrderApi => {
    return getApiInstance('OrderApi', OrderApi);
};

/** Возвращает клиента API задач заказов */
export const getOrderTaskApi = (): OrderTaskApi => {
    return getApiInstance('OrderTaskApi', OrderTaskApi);
};

/** Возвращает клиента API политик */
export const getPoliciesApi = (): PoliciesApi => {
    return getApiInstance('PoliciesApi', PoliciesApi);
};

/** Возвращает клиента API платежей */
export const getPaymentsApi = (): PaymentsApi => {
    return getApiInstance('PaymentsApi', PaymentsApi);
};

/** Возвращает клиента API прайс-листов */
export const getPriceListApi = (): PriceListApi => {
    return getApiInstance('PriceListApi', PriceListApi);
};

/** Возвращает клиента API промокодов */
export const getPromoCodeApi = (): PromoCodeApi => {
    return getApiInstance('PromoCodeApi', PromoCodeApi);
};

/** Возвращает клиента API шаблонов промокодов */
export const getPromoCodeTemplateApi = (): PromoCodeTemplateApi => {
    return getApiInstance('PromoCodeTemplateApi', PromoCodeTemplateApi);
};

/** Возвращает клиента API тиражей промокодов */
export const getPromoCodePrintingApi = (): PromoCodePrintingApi => {
    return getApiInstance('PromoCodePrintingApi', PromoCodePrintingApi);
};

/** Возвращает клиента API отзывов */
export const getReviewsApi = (): ReviewsApi => {
    return getApiInstance('ReviewsApi', ReviewsApi);
};

/** Возвращает клиента API подписок */
export const getSubscriptionApi = (): SubscriptionApi => {
    return getApiInstance('SubscriptionApi', SubscriptionApi);
};

/** Возвращает клиент загрузки и удаления медиафайлов. */
export const getMediaApi = (): MediaApi => {
    return getApiInstance('MediaApi', MediaApi);
};

/** Возвращает клиента API справочников */
export const getEnumApi = (): EnumApi => {
    return getApiInstance('EnumApi', EnumApi);
};

/** Возвращает клиент чтения показателей памяти. */
export const getMemoryDiagnosticsApi = (): MemoryDiagnosticsApi => {
    return getApiInstance('MemoryDiagnosticsApi', MemoryDiagnosticsApi);
};

// Функция принудительного обновления всех инстансов
/** Обновляет сессию используемых клиентов API */
export const refreshAllApiInstances = (): void => {
    const newConfig = createConfig();
    const token = getAccessToken();

    for (const instance of apiCache.values()) {
        if (Reflect.has(instance, 'configuration')) {
            Reflect.set(instance, 'configuration', newConfig);
        }
        const axios = Reflect.get(instance, 'axios') as AxiosInstance | undefined;

        if (axios && token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }
};

/** Удаляет сохранённые клиенты API */
export const clearApiCache = (): void => {
    apiCache.clear();
};

/** Возвращает клиента периодов цен и промоакций. */
export const getPricingPeriodsApi = (): PricingPeriodsApi => getApiInstance(
    'PricingPeriodsApi',
    PricingPeriodsApi,
);

/** Возвращает клиента библиотеки промобаннеров. */
export const getPromotionBannerTemplatesApi = (): PromotionBannerTemplatesApi => getApiInstance(
    'PromotionBannerTemplatesApi',
    PromotionBannerTemplatesApi,
);

/** Возвращает клиента управления планировщиком */
export const getSchedulerApi = (): SchedulerApi => getApiInstance('SchedulerApi', SchedulerApi);

/** Возвращает клиента прикладных настроек */
export const getVoxSettingsApi = (): VoxSettingsApi => getApiInstance('VoxSettingsApi', VoxSettingsApi);

/** Возвращает клиент личных действий переписки */
export const getChatPersonalizationApi = (): ChatPersonalizationApi =>
    getApiInstance('ChatPersonalizationApi', ChatPersonalizationApi);

/** Возвращает клиента проверки мобильных переходов */
export const getHelpPushApi = (): HelpPushApi => getApiInstance('HelpPushApi', HelpPushApi);

/** Возвращает клиента отправки пользовательских уведомлений */
export const getNotificationApi = (): NotificationApi => getApiInstance('NotificationApi', NotificationApi);

/** Возвращает клиента мобильной переписки */
export const getAppChatApi = (): AppChatApi => getApiInstance('AppChatApi', AppChatApi);

/** Возвращает клиента текстовок приложения */
export const getMessageTemplatesApi = (): MessageTemplatesApi =>
    getApiInstance('MessageTemplatesApi', MessageTemplatesApi);

/** Возвращает клиента управления кэшем */
export const getCacheManagementApi = (): CacheManagementApi =>
    getApiInstance('CacheManagementApi', CacheManagementApi);

/** Возвращает клиента шаблонов сообщений чата */
export const getChatReplyTemplatesApi = (): ChatReplyTemplatesApi =>
    getApiInstance('ChatReplyTemplatesApi', ChatReplyTemplatesApi);

/** Возвращает клиента медиатеки */
export const getMediaLibraryApi = (): MediaLibraryApi => getApiInstance(
    'MediaLibraryApi',
    MediaLibraryApi,
);

/** Возвращает клиента аналитики отменённых заказов */
export const getOrderCancellationAnalyticsApi = (): OrderCancellationAnalyticsApi =>
    getApiInstance('OrderCancellationAnalyticsApi', OrderCancellationAnalyticsApi);
